import { jsx as _jsx } from "react/jsx-runtime";
import React, { useMemo, useRef, useState } from 'react';
import { t } from 'i18next';
import FractionTile from './FractionTile';
import { getMaxPartsForDenominator, getDenominatorFromFractionType, } from './SplitFractionTileModal';
import ButtonComponent from './ButtonComponent';
import globalStore from '../store';
import { getSnappedPosition, solveDockMatchingScale, getGlobalFractionTileScale, } from '../helper/snapHelper';
import { snapTileToNumberLine } from '../helper/numberLineHelper';
import { sumAndReduceFractions, getFractionDimensions, parseFraction, getFractionAriaText } from '../helper/helper';
import { playAudio } from '../helper/audioHelper';
var polarToCartesian = function (centerX, centerY, radius, angleInDegrees) {
    var angleInRadians = (angleInDegrees * Math.PI) / 180.0;
    return {
        x: centerX + radius * Math.cos(angleInRadians),
        y: centerY + radius * Math.sin(angleInRadians),
    };
};
var describeArc = function (x, y, radius, startAngle, endAngle) {
    var start = polarToCartesian(x, y, radius, endAngle);
    var end = polarToCartesian(x, y, radius, startAngle);
    var largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';
    var d = [
        'M',
        x,
        y,
        'L',
        start.x,
        start.y,
        'A',
        radius,
        radius,
        0,
        largeArcFlag,
        0,
        end.x,
        end.y,
        'Z',
    ].join(' ');
    return d;
};
var FractionButton = function (_a) {
    var _b, _c, _d;
    var tool = _a.tool, index = _a.index, manipulative = _a.manipulative, selectedTool = _a.selectedTool, closeManipulatorPopup = _a.closeManipulatorPopup, customEventHandler = _a.customEventHandler;
    var _e = globalStore.useStoreState(function (store) { return store.playerStore; }), zoomLevel = _e.zoomLevel, playerBgPattern = _e.playerBgPattern;
    var _f = useState(false), isDragging = _f[0], setIsDragging = _f[1];
    var justDroppedRef = useRef(false);
    var splitLabel = tool.ariaLabel.split('/');
    var numerator = splitLabel[0];
    var denominator = splitLabel[1] || 0;
    // Resolve selectedTool to concrete fraction-tile ids: a manipulativeGroup_
    // entry (the group is the active selection) expands to its member tile ids —
    // otherwise every lookup below silently treats a grouped selection as "no
    // fraction tile selected" (canGlue/canSplit/activeTile all fall through to
    // their disabled defaults), since no manipulative item's id equals a groupId.
    var resolvedFtIds = useMemo(function () {
        return selectedTool.flatMap(function (sid) {
            if (sid.startsWith('manipulativeGroup_')) {
                return manipulative
                    .filter(function (m) { return m.data.type === 'fractionTiles' && m.data.groupId === sid; })
                    .map(function (m) { return m.data.id; });
            }
            var stripped = sid.replace(/_custom_label_(rect|circle)_\d+$/, '');
            return stripped.startsWith('fractionTiles_') ? [stripped] : [];
        });
    }, [selectedTool, manipulative]);
    var selectedManipulativeId = useMemo(function () { return resolvedFtIds[0]; }, [resolvedFtIds]);
    var activeTile = (_b = manipulative.find(function (m) { return m.data.type === 'fractionTiles' && m.data.id === selectedManipulativeId; })) === null || _b === void 0 ? void 0 : _b.data;
    // When selectedTool holds a dummy ID (no real tile yet), parse shape/label from the ID.
    // Format: fractionTiles_{shapeType}_{lablesType}_dummyForRightTool
    var parsedDummy = useMemo(function () {
        if (!(selectedManipulativeId === null || selectedManipulativeId === void 0 ? void 0 : selectedManipulativeId.endsWith('_dummyForRightTool')))
            return null;
        var parts = selectedManipulativeId.split('_');
        return {
            shapeType: parts[1],
            lablesType: parts[2],
        };
    }, [selectedManipulativeId]);
    var isRectangle = activeTile
        ? activeTile.shapeType === 'rectangle'
        : (parsedDummy === null || parsedDummy === void 0 ? void 0 : parsedDummy.shapeType) !== 'circle';
    var isLabeled = activeTile
        ? activeTile.lablesType === 'labeled'
        : (parsedDummy === null || parsedDummy === void 0 ? void 0 : parsedDummy.lablesType) === 'labeled';
    // Get size from fractionButtonSizes record
    var config = getFractionDimensions(tool.id);
    var buttonSize = isRectangle
        ? config
            ? { width: config.button.width, height: config.button.height }
            : { width: 120, height: 50 }
        : { width: 60, height: 60 };
    var handleDragStart = function (clientX, clientY, currentTarget) {
        var _a, _b;
        setIsDragging(true);
        closeManipulatorPopup();
        var target = currentTarget;
        var hasMoved = false;
        // Match the GLOBAL scale of existing tiles of the same shape type — a
        // tile dragged in from the panel always starts at the shared fraction-tile
        // scale. Number-line-attached tiles are skipped: their scale is forced by
        // the line, so taking the first tile of the shape regardless (as this used
        // to) made the panel inherit a number line's scale.
        var dragShapeType = isRectangle ? 'rectangle' : 'circle';
        var tileScale = getGlobalFractionTileScale(manipulative, dragShapeType);
        // Create a visual clone that follows the cursor to show exact shape
        var clone = target.cloneNode(true);
        clone.classList.add('fraction-tile-clone');
        clone.removeAttribute('id'); // Avoid ID conflicts
        clone.querySelectorAll('.tooltipContainer').forEach(function (el) { return el.remove(); });
        // Apply dimensions for rectangle drag if applicable
        if (isRectangle) {
            var dims = (_a = getFractionDimensions(tool.id)) === null || _a === void 0 ? void 0 : _a.rectangle;
            if (dims) {
                clone.style.width = "".concat(dims.width * tileScale * zoomLevel, "px");
                clone.style.height = "".concat(dims.height * tileScale * zoomLevel, "px");
                clone.style.borderWidth = "".concat(3 * zoomLevel, "px");
                // Match FractionTileManipulative styles for rectangle
                clone.style.backgroundColor = '#fff';
                clone.classList.add('fraction-tile-rect');
                var labelEl = clone.querySelector('.fraction-tile-label');
                if (labelEl) {
                    labelEl.classList.add('fraction-tile-clone-label', 'rect-centered');
                    labelEl.style.fontSize = "".concat(1.5625 * tileScale * zoomLevel, "rem");
                }
            }
        }
        else if (!activeTile || activeTile.shapeType === 'circle') {
            // Default to circle size if not rectangle (matches FractionTileManipulative logic)
            var size = 300;
            clone.style.width = "".concat(size * tileScale * zoomLevel, "px");
            clone.style.height = "".concat(size * tileScale * zoomLevel, "px");
            // Rebuild content for high-res Circle style
            var circleConfig = (_b = getFractionDimensions(tool.id)) === null || _b === void 0 ? void 0 : _b.circle;
            var config_1 = circleConfig || { start: 0, sweep: 360 };
            var radius = 150;
            var center = 150;
            var isFullCircle = config_1.sweep === 360;
            // Preserve the label element
            var labelEl = clone.querySelector('.fraction-tile-label');
            var labelClone = labelEl ? labelEl.cloneNode(true) : null;
            // Build SVG string
            var commonProps = "fill=\"#fff\" stroke=\"#293273\" stroke-width=\"3\"";
            var pathData = '';
            if (isFullCircle) {
                pathData = "<circle cx=\"".concat(center, "\" cy=\"").concat(center, "\" r=\"").concat(radius, "\" ").concat(commonProps, " />");
            }
            else {
                var d = describeArc(center, center, radius, config_1.start, config_1.start + config_1.sweep);
                pathData = "<path d=\"".concat(d, "\" ").concat(commonProps, " />");
            }
            var svgString = "<svg width=\"100%\" height=\"100%\" viewBox=\"0 0 300 300\" style=\"overflow: visible;\">".concat(pathData, "</svg>");
            clone.innerHTML = svgString;
            // Re-insert label
            if (labelClone) {
                var midAngle = config_1.start + config_1.sweep / 2;
                var labelRadius = 0;
                if (!isFullCircle) {
                    var alpha = (config_1.sweep / 2) * (Math.PI / 180);
                    var safeAlpha = alpha === 0 ? 1 : alpha;
                    labelRadius = (2 * radius * Math.sin(safeAlpha)) / (3 * safeAlpha);
                }
                var labelPos = polarToCartesian(center, center, labelRadius, midAngle);
                var labelWrapper = document.createElement('div');
                labelWrapper.classList.add('fraction-tile-label-wrapper');
                labelWrapper.style.left = "".concat(labelPos.x * tileScale * zoomLevel, "px");
                labelWrapper.style.top = "".concat(labelPos.y * tileScale * zoomLevel, "px");
                // Calculate Rotation
                var absoluteTextAngle = (midAngle + 90) % 360;
                if (absoluteTextAngle < 0)
                    absoluteTextAngle += 360;
                var finalRotation = absoluteTextAngle;
                var roundedAngle = Math.round(absoluteTextAngle);
                if (roundedAngle > 90 && roundedAngle < 270) {
                    finalRotation += 180;
                }
                if (denominator === 0 || Number(numerator) === Number(denominator)) {
                    finalRotation = 0;
                }
                labelWrapper.style.transform = "translate(-50%, -50%) rotate(".concat(finalRotation, "deg)");
                // Reset any styles on the labelClone that might conflict
                labelClone.style.marginTop = '0';
                labelClone.style.marginLeft = '0';
                labelClone.style.fontSize = "".concat(1.5625 * tileScale * zoomLevel, "rem");
                labelClone.classList.add('fraction-tile-clone-label');
                // Apply max width for label to match workspace wrapping
                var alphaChord = (config_1.sweep / 2) * (Math.PI / 180);
                var chord = 2 * labelRadius * Math.sin(alphaChord);
                var sectorMaxWidth = isFullCircle ? 240 : chord * 0.95;
                var circleLabelWidth = Math.max(20, sectorMaxWidth);
                labelClone.style.maxWidth = "".concat(circleLabelWidth * tileScale * zoomLevel, "px");
                labelWrapper.appendChild(labelClone);
                clone.appendChild(labelWrapper);
            }
        }
        // Initial position (center on cursor)
        var updateClonePosition = function (clientX, clientY) {
            var rect = clone.getBoundingClientRect();
            clone.style.left = "".concat(clientX - rect.width / 2, "px");
            clone.style.top = "".concat(clientY - rect.height / 2, "px");
        };
        updateClonePosition(clientX, clientY);
        var ghostClone = clone.cloneNode(true);
        ghostClone.classList.add('fraction-tile-ghost');
        if (isRectangle) {
            var inner = ghostClone.querySelector('.fraction-tile-rect-inner') || ghostClone;
            inner.style.borderStyle = 'dashed';
        }
        else {
            var shapes = ghostClone.querySelectorAll('path, circle');
            shapes.forEach(function (s) { return s.setAttribute('stroke-dasharray', '5,5'); });
        }
        var handleMove = function (x, y) {
            var _a, _b, _c;
            hasMoved = true;
            updateClonePosition(x, y);
            if (!clone.parentElement) {
                document.body.appendChild(clone);
                document.body.appendChild(ghostClone);
            }
            var scrollEl = document.querySelector('.whiteboardScrollbar .customScrollbarContent');
            var mainBoard = document.querySelector('.mainHolder');
            var canvasEl = mainBoard || scrollEl;
            if (canvasEl) {
                var rect = canvasEl.getBoundingClientRect();
                var baseDims = isRectangle
                    ? ((_a = getFractionDimensions(tool.id)) === null || _a === void 0 ? void 0 : _a.rectangle) || { width: 120, height: 55 }
                    : { width: 300, height: 300 };
                var dims = { width: baseDims.width * tileScale, height: baseDims.height * tileScale };
                // Convert screen mouse coordinates to canvas coordinates.
                // mainHolder's rect already accounts for scroll and margin: auto centering.
                var px = (x - rect.left) / zoomLevel;
                var py = (y - rect.top) / zoomLevel;
                var canvasX = px - dims.width / 2;
                var canvasY = py - dims.height / 2;
                // Determine the size to dock/preview at, BEFORE tile-to-tile docking
                // below — without this the ghost is sized using the inherited
                // `tileScale` the whole time, never previewing the size it will
                // actually land at (createFractionTile runs the real check
                // independently on drop). Prefer matching an adjacent tile's own
                // rendered scale directly — that neighbor's size is ground truth for
                // what a docked tile must match, regardless of what put it at that
                // size. Only fall back to a number-line proximity guess when there's
                // no neighbor tile to match yet (e.g. dropping the very first tile
                // onto an empty stretch of the line).
                var prospectiveNlSnap = isRectangle
                    ? snapTileToNumberLine({ x: canvasX, y: canvasY }, baseDims, tileScale, manipulative, 'new')
                    : null;
                var baselineDockDims = prospectiveNlSnap
                    ? {
                        width: baseDims.width * prospectiveNlSnap.scale,
                        height: baseDims.height * prospectiveNlSnap.scale,
                    }
                    : dims;
                // Two-pass solve — the same call the on-canvas drag and the create-on-
                // drop path (FractionTileController) make, so the panel-drag ghost is
                // sized to the tile it will dock against BEFORE the dock x/y is
                // computed, and matches what actually gets created.
                var dockDims = baselineDockDims;
                var adjacent = void 0;
                var snapped = void 0;
                if (isRectangle) {
                    var solved = solveDockMatchingScale({ x: canvasX, y: canvasY }, baselineDockDims, baseDims, manipulative, 'new', undefined, { fractionSize: tool.id });
                    snapped = solved.snap;
                    dockDims = solved.dimensions;
                    adjacent = solved.dockedTo;
                }
                else {
                    snapped = getSnappedPosition({ x: canvasX, y: canvasY }, dockDims, 'circle', manipulative, 'new', undefined, { fractionSize: tool.id });
                }
                // Helper: Check if mouse is currently over the canvas area
                var isOverCanvas = x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
                if (isOverCanvas) {
                    // Show ghost on canvas for both Rectangles and Circles
                    var showGhost = true;
                    if (showGhost) {
                        // --- GRID SNAPPING LOGIC FOR GHOST ---
                        var finalX = snapped.x;
                        var finalY = snapped.y;
                        var isGridSnapped = false;
                        var GRID_SIZE = 55;
                        if (playerBgPattern === 'square' &&
                            isRectangle &&
                            !snapped.isSnappedX &&
                            !snapped.isSnappedY) {
                            finalX = Math.round(canvasX / GRID_SIZE) * GRID_SIZE;
                            finalY = Math.round(canvasY / GRID_SIZE) * GRID_SIZE;
                            isGridSnapped = true;
                        }
                        // -------------------------------------
                        // --- GHOST SCALE/POSITION RESOLUTION ---
                        // When docked to a neighbor (adjacentScale found above), that
                        // neighbor's own size is already ground truth — dockDims used it
                        // for the docking computation above, so `snapped.x/y` is already
                        // geometrically correct and needs no further correction, just a
                        // resize of the ghost element to match. Only when there's no
                        // neighbor to match (adjacentScale null) do we fall back to the
                        // number-line proximity check for both size and position, same
                        // priority as createFractionTile: the NL's own y is authoritative
                        // there since docking's y assumed a guessed (possibly wrong) size.
                        var isTileDocked = snapped.isSnappedX || snapped.isSnappedY;
                        var nlSnap = null;
                        var resolvedScale = null;
                        if (isRectangle) {
                            if (isTileDocked && adjacent) {
                                resolvedScale = adjacent.scale;
                            }
                            else {
                                nlSnap = snapTileToNumberLine(isTileDocked ? { x: finalX, y: finalY } : { x: canvasX, y: canvasY }, baseDims, tileScale, manipulative, 'new');
                                if (nlSnap) {
                                    if (!isTileDocked) {
                                        finalX = nlSnap.x;
                                    }
                                    finalY = nlSnap.y;
                                    resolvedScale = nlSnap.scale;
                                }
                            }
                            if (resolvedScale !== null) {
                                var ghostW = baseDims.width * resolvedScale * zoomLevel;
                                var ghostH = baseDims.height * resolvedScale * zoomLevel;
                                ghostClone.style.width = "".concat(ghostW, "px");
                                ghostClone.style.height = "".concat(ghostH, "px");
                                var ghostInner = ghostClone.querySelector('.fraction-tile-rect-inner');
                                if (ghostInner) {
                                    ghostInner.style.width = "".concat(ghostW, "px");
                                    ghostInner.style.height = "".concat(ghostH, "px");
                                }
                                var ghostLabel = ghostClone.querySelector('.fraction-tile-clone-label');
                                if (ghostLabel) {
                                    ghostLabel.style.fontSize = "".concat(1.5625 * resolvedScale * zoomLevel, "rem");
                                }
                            }
                        }
                        // --- END GHOST SCALE/POSITION RESOLUTION ---
                        var isSnapped = snapped.isSnappedX || snapped.isSnappedY || isGridSnapped || resolvedScale !== null;
                        if (!isSnapped) {
                            ghostClone.style.display = 'none';
                        }
                        else {
                            var screenX_1 = finalX * zoomLevel + rect.left - canvasEl.scrollLeft;
                            var screenY_1 = finalY * zoomLevel + rect.top - canvasEl.scrollTop;
                            ghostClone.style.left = "".concat(screenX_1, "px");
                            ghostClone.style.top = "".concat(screenY_1, "px");
                            ghostClone.style.display = 'block';
                        }
                        if (!isRectangle) {
                            // Update circle SVG path/rotation for the ghost preview
                            var svg = ghostClone.querySelector('svg');
                            if (svg) {
                                var circleConfig = (_b = getFractionDimensions(tool.id)) === null || _b === void 0 ? void 0 : _b.circle;
                                var config_2 = circleConfig || { start: 0, sweep: 360 };
                                var radius = 150;
                                var center = 150;
                                var isFull = config_2.sweep === 360;
                                // Use snapped rotation if available, otherwise maintain current
                                var rotationOffset = snapped.rotation !== undefined ? snapped.rotation : ((_c = activeTile === null || activeTile === void 0 ? void 0 : activeTile.rotation) !== null && _c !== void 0 ? _c : 0);
                                var finalStart = (config_2.start + rotationOffset) % 360;
                                var newPath = void 0;
                                var dashProps = 'stroke-dasharray="5,5"';
                                if (isFull) {
                                    newPath = "<circle cx=\"".concat(center, "\" cy=\"").concat(center, "\" r=\"").concat(radius, "\" fill=\"#fff\" stroke=\"#293273\" stroke-width=\"3\" ").concat(dashProps, " />");
                                }
                                else {
                                    var d = describeArc(center, center, radius, finalStart, finalStart + config_2.sweep);
                                    newPath = "<path d=\"".concat(d, "\" fill=\"#fff\" stroke=\"#293273\" stroke-width=\"3\" ").concat(dashProps, " />");
                                }
                                svg.innerHTML = newPath;
                                // Adjust label position based on the new rotation
                                var labelWrapper = ghostClone.querySelector('.fraction-tile-label-wrapper');
                                if (labelWrapper) {
                                    var midAngle = finalStart + config_2.sweep / 2;
                                    var labelRadius = 0;
                                    if (!isFull) {
                                        var alpha = (config_2.sweep / 2) * (Math.PI / 180);
                                        var safeAlpha = alpha === 0 ? 1 : alpha;
                                        labelRadius = (2 * radius * Math.sin(safeAlpha)) / (3 * safeAlpha);
                                    }
                                    var labelPos = polarToCartesian(center, center, labelRadius, midAngle);
                                    labelWrapper.style.left = "".concat(labelPos.x * tileScale * zoomLevel, "px");
                                    labelWrapper.style.top = "".concat(labelPos.y * tileScale * zoomLevel, "px");
                                    // Calculate Rotation
                                    var absoluteTextAngle = (midAngle + 90) % 360;
                                    if (absoluteTextAngle < 0)
                                        absoluteTextAngle += 360;
                                    var finalRotation = absoluteTextAngle;
                                    var roundedAngle = Math.round(absoluteTextAngle);
                                    if (roundedAngle > 90 && roundedAngle < 270) {
                                        finalRotation += 180;
                                    }
                                    if (denominator === 0 || Number(numerator) === Number(denominator)) {
                                        finalRotation = 0;
                                    }
                                    labelWrapper.style.transform =
                                        "translate(-50%, -50%) rotate(".concat(finalRotation, "deg)");
                                    // Apply max width for label to match workspace wrapping
                                    var labelClone = labelWrapper.querySelector('.fraction-tile-label');
                                    if (labelClone) {
                                        var alphaChord = (config_2.sweep / 2) * (Math.PI / 180);
                                        var chord = 2 * labelRadius * Math.sin(alphaChord);
                                        var sectorMaxWidth = isFull ? 240 : chord * 0.95;
                                        var circleLabelWidth = Math.max(20, sectorMaxWidth);
                                        labelClone.style.maxWidth = "".concat(circleLabelWidth * tileScale * zoomLevel, "px");
                                    }
                                }
                            }
                        }
                    }
                    else {
                        ghostClone.style.display = 'none';
                    }
                }
                else {
                    ghostClone.style.display = 'none';
                }
            }
        };
        var handleEnd = function (endX, endY) {
            var _a, _b, _c;
            setIsDragging(false);
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', handleTouchEnd);
            if (clone.parentElement) {
                document.body.removeChild(clone);
            }
            if (ghostClone.parentElement) {
                document.body.removeChild(ghostClone);
            }
            if (hasMoved) {
                justDroppedRef.current = true;
                setTimeout(function () { justDroppedRef.current = false; }, 0);
                // Dispatch event to create a new fraction tile at the drop location
                customEventHandler.dispatchEvent('bottomPanel', {
                    id: 'createFractionTile',
                    type: 'fractionBtn',
                    data: {
                        fractionType: tool.id,
                        numerator: Number(numerator),
                        denominator: Number(denominator),
                        dropX: endX,
                        dropY: endY,
                        shapeType: isRectangle ? 'rectangle' : 'circle',
                        lablesType: (_b = (_a = activeTile === null || activeTile === void 0 ? void 0 : activeTile.lablesType) !== null && _a !== void 0 ? _a : parsedDummy === null || parsedDummy === void 0 ? void 0 : parsedDummy.lablesType) !== null && _b !== void 0 ? _b : 'labeled',
                        color: '#fff',
                    },
                });
                // Blur the button so a subsequent Space press doesn't re-trigger it
                (_c = document.activeElement) === null || _c === void 0 ? void 0 : _c.blur();
            }
        };
        var handleMouseMove = function (moveEvent) {
            handleMove(moveEvent.clientX, moveEvent.clientY);
        };
        var handleTouchMove = function (moveEvent) {
            moveEvent.preventDefault();
            handleMove(moveEvent.touches[0].clientX, moveEvent.touches[0].clientY);
        };
        var handleMouseUp = function (upEvent) {
            handleEnd(upEvent.clientX, upEvent.clientY);
        };
        var handleTouchEnd = function (upEvent) {
            var touch = upEvent.changedTouches[0];
            handleEnd(touch.clientX, touch.clientY);
        };
        document.addEventListener('mousemove', handleMouseMove, { passive: false });
        document.addEventListener('mouseup', handleMouseUp);
        document.addEventListener('touchmove', handleTouchMove, { passive: false });
        document.addEventListener('touchend', handleTouchEnd);
    };
    var handleMouseDown = function (e) {
        handleDragStart(e.clientX, e.clientY, e.currentTarget);
    };
    var handleTouchStart = function (e) {
        handleDragStart(e.touches[0].clientX, e.touches[0].clientY, e.currentTarget);
    };
    var handleClick = function () {
        var _a, _b;
        // VoiceOver double-tap synthesizes a click event. Ignore if a real drag just completed.
        if (justDroppedRef.current)
            return;
        closeManipulatorPopup();
        var el = document.querySelector('.whiteboardScrollbar .customScrollbarContent');
        var r = el === null || el === void 0 ? void 0 : el.getBoundingClientRect();
        customEventHandler.dispatchEvent('bottomPanel', {
            id: 'createFractionTile',
            type: 'fractionBtn',
            data: {
                fractionType: tool.id,
                numerator: Number(numerator),
                denominator: Number(denominator),
                dropX: r ? r.left + r.width / 2 : 400,
                dropY: r ? r.top + r.height / 2 : 300,
                shapeType: isRectangle ? 'rectangle' : 'circle',
                lablesType: (_b = (_a = activeTile === null || activeTile === void 0 ? void 0 : activeTile.lablesType) !== null && _a !== void 0 ? _a : parsedDummy === null || parsedDummy === void 0 ? void 0 : parsedDummy.lablesType) !== null && _b !== void 0 ? _b : (isLabeled ? 'labeled' : 'custom'),
                color: '#fff',
            },
        });
    };
    var canGlue = false;
    var canUnglue = false;
    // Filter relevant selected items
    var selectedFractionTiles = resolvedFtIds
        .map(function (id) { return manipulative.find(function (m) { return m.data.id === id; }); })
        .filter(function (m) { return !!m && m.data.type === 'fractionTiles'; });
    var selectedCircleTiles = selectedFractionTiles.filter(function (m) { return m.data.shapeType === 'circle'; });
    var selectedRectTiles = selectedFractionTiles.filter(function (m) { return m.data.shapeType === 'rectangle'; });
    var isTileWhole = function (t) {
        var _a, _b, _c;
        var td = t.data;
        var effectiveSize = td.gluedFrom && td.gluedFrom.length > 0
            ? sumAndReduceFractions(td.gluedFrom.map(function (f) { return f.fractionSize; }))
            : td.fractionSize;
        var parsed = parseFraction(effectiveSize);
        var fn = (_b = (_a = td.customNumerator) !== null && _a !== void 0 ? _a : parsed === null || parsed === void 0 ? void 0 : parsed.numerator) !== null && _b !== void 0 ? _b : 1;
        var fd = (_c = td.customDenominator) !== null && _c !== void 0 ? _c : getDenominatorFromFractionType(effectiveSize);
        return fd !== 0 && fn === fd;
    };
    var allWholeDisabled = false;
    if (selectedCircleTiles.length >= 2) {
        var totalSweep_1 = 0;
        selectedCircleTiles.forEach(function (t) {
            var _a, _b, _c, _d;
            var td = t.data;
            totalSweep_1 += (_d = (_a = td.customSweep) !== null && _a !== void 0 ? _a : (_c = (_b = getFractionDimensions(td.fractionSize)) === null || _b === void 0 ? void 0 : _b.circle) === null || _c === void 0 ? void 0 : _c.sweep) !== null && _d !== void 0 ? _d : 0;
        });
        if (totalSweep_1 <= 360 + 1e-9) {
            canGlue = true;
        }
        else if (selectedCircleTiles.every(isTileWhole)) {
            allWholeDisabled = true;
        }
    }
    else if (selectedRectTiles.length >= 2) {
        if (selectedRectTiles.every(isTileWhole)) {
            allWholeDisabled = true;
        }
        else {
            canGlue = true;
        }
    }
    var gluedTiles = selectedFractionTiles.filter(function (t) { return t.data.gluedFrom && t.data.gluedFrom.length > 0; });
    // "Unglue" only makes sense for a single selected compound tile (split it back
    // into its pieces). With 2+ tiles selected — even if every one of them already
    // happens to be a previously-glued compound piece — the only sensible action is
    // to glue them together into a bigger piece. Without this guard, selecting
    // multiple already-glued tiles set canUnglue = true and disabled Combine Tiles
    // (isGlueDisabled = !canGlue || canUnglue), blocking a perfectly valid glue.
    if (gluedTiles.length > 0 && selectedFractionTiles.length === gluedTiles.length &&
        selectedFractionTiles.length === 1) {
        canUnglue = true;
    }
    // Determine the selected tile's fraction to check if split is possible.
    // Split only makes sense for exactly one underlying tile — resolvedFtIds
    // already expands a group selection to its members, so this correctly
    // disables Split for a real multi-member group (same as a multi-tile
    // non-group selection) and enables it for a singleton (1-member) group.
    var selectedTile = resolvedFtIds.length === 1
        ? manipulative.find(function (m) { return m.data.type === 'fractionTiles' && m.data.id === resolvedFtIds[0]; })
        : null;
    var selectedTileData = selectedTile === null || selectedTile === void 0 ? void 0 : selectedTile.data;
    var selectedFractionType = selectedTileData
        ? selectedTileData.gluedFrom && selectedTileData.gluedFrom.length > 0
            ? sumAndReduceFractions(selectedTileData.gluedFrom.map(function (f) { return f.fractionSize; }))
            : selectedTileData.fractionSize
        : null;
    var selectedNumerator = selectedFractionType
        ? ((_d = (_c = parseFraction(selectedFractionType)) === null || _c === void 0 ? void 0 : _c.numerator) !== null && _d !== void 0 ? _d : 1)
        : 1;
    var selectedDenominator = selectedFractionType
        ? getDenominatorFromFractionType(selectedFractionType)
        : 0;
    var maxSplitParts = selectedDenominator > 0 ? getMaxPartsForDenominator(selectedDenominator) : 0;
    var isSmallTile = useMemo(function () {
        var _a, _b, _c, _d;
        if (!selectedTileData)
            return false;
        var gluedFrom = selectedTileData.gluedFrom, fractionSize = selectedTileData.fractionSize, shapeType = selectedTileData.shapeType;
        var finalFractionSize = (gluedFrom === null || gluedFrom === void 0 ? void 0 : gluedFrom.length)
            ? sumAndReduceFractions(gluedFrom.map(function (f) { return f.fractionSize; }))
            : fractionSize;
        var config = getFractionDimensions(finalFractionSize);
        if (!config)
            return false;
        if (shapeType === 'rectangle') {
            return ((_b = (_a = config.rectangle) === null || _a === void 0 ? void 0 : _a.width) !== null && _b !== void 0 ? _b : 0) < 32;
        }
        return ((_d = (_c = config.circle) === null || _c === void 0 ? void 0 : _c.sweep) !== null && _d !== void 0 ? _d : 0) < 20;
    }, [selectedTileData]);
    var canSplit = maxSplitParts >= 2 && !isSmallTile;
    if (tool.id === 'fraction-tile-scissors') {
        return (_jsx("div", { className: "fraction-button-wrapper", children: _jsx(ButtonComponent, { defaultCSS: "toolBtn", normalCSS: "toolBtn", ariaLabel: t(tool.ariaLabel), title: tool.ariaLabel, disabled: !canSplit, showToolTip: true, direction: 'left', isParentRelative: false, imgSrc: tool.imgSrc, clickHandler: function () {
                    if (!canSplit)
                        return;
                    closeManipulatorPopup();
                    customEventHandler.dispatchEvent('bottomPanel', {
                        id: 'splitFractionTile',
                        data: {
                            currentDenominator: selectedDenominator,
                            currentNumerator: selectedNumerator,
                        },
                    });
                } }, tool.id) }));
    }
    if (tool.id === 'fraction-tile-glue') {
        var isGlueDisabled_1 = !canGlue || canUnglue;
        var wholeCount = selectedRectTiles.length >= 2
            ? selectedRectTiles.length
            : selectedCircleTiles.length;
        var glueAriaLabel = allWholeDisabled
            ? t('text.wholeTilesCannotCombine', {
                count: wholeCount,
                defaultValue: '{{count}} whole tiles selected, cannot be combined',
            })
            : t(tool.ariaLabel);
        return (_jsx("div", { className: "fraction-button-wrapper", children: _jsx(ButtonComponent, { defaultCSS: "toolBtn", normalCSS: "toolBtn", ariaLabel: glueAriaLabel, title: glueAriaLabel, disabled: isGlueDisabled_1 || allWholeDisabled, showToolTip: true, direction: 'left', isParentRelative: false, imgSrc: tool.imgSrc, clickHandler: function () {
                    if (isGlueDisabled_1 || allWholeDisabled)
                        return;
                    closeManipulatorPopup();
                    if (canUnglue && !canGlue) {
                        playAudio('scissor');
                    }
                    customEventHandler.dispatchEvent('bottomPanel', {
                        id: 'glueFractionTiles',
                        type: 'fractionBtn',
                    });
                } }, tool.id) }));
    }
    return (_jsx(React.Fragment, { children: _jsx(FractionTile, { numerator: Number(numerator), denominator: Number(denominator), integer: splitLabel.length === 1 ? Number(numerator) : undefined, color: "#fff", fractionType: tool.id, isLabeled: isLabeled, width: buttonSize.width, height: buttonSize.height, onMouseDown: handleMouseDown, onTouchStart: handleTouchStart, onClick: handleClick, shapeType: isRectangle ? 'rectangle' : 'circle', ariaLabel: getFractionAriaText(Number(numerator), Number(denominator), isRectangle ? 'rectangle' : 'circle'), tabIndex: 0, showTooltip: !isDragging && isLabeled, imageSource: !isRectangle ? "./assets/images/fraction-tiles/circle-".concat(tool.id, ".svg") : undefined }) }, "".concat(tool.id, "_").concat(index)));
};
export default FractionButton;
